import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PageContainer,
  Button,
  Input,
  Toggle,
  Skeleton,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { GLASS, cn } from '@/constants/design-tokens';
import { productService } from '@/services/product.service';
import {
  variantAttributeService,
  VARIANT_ATTRIBUTES_QUERY_KEY,
} from '@/services/variant-attribute.service';
import type { VariantAttribute, VariantAttributeValue } from '@/types/product';

/**
 * Tenant library of reusable variant attributes (Size, Colour, Gauge, …) and
 * their values. Whatever is defined on one product's variants lands here and
 * is offered when building variants for any other product; this page manages
 * the library on its own. Nothing is deleted — deactivated entries are hidden
 * from pickers while existing product snapshots keep working.
 */

// ─── Icons ──────────────────────────────────────────────────────────────────

function Icon({ d, className = 'h-5 w-5' }: { d: string; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

const ICON_TAG = 'M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z';
const ICON_LIST = 'M4 6h16M4 10h16M4 14h16M4 18h16';
const ICON_BOX = 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4';

// ─── KPI card ───────────────────────────────────────────────────────────────

function Kpi({ label, value, iconPath, gradient }: { label: string; value: string; iconPath: string; gradient: string }) {
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 p-5 border border-neutral-100 dark:border-neutral-700 animate-fade-in">
      <div className="flex items-center gap-3 mb-3">
        <div className={cn('w-10 h-10 rounded-lg bg-gradient-to-br flex items-center justify-center text-white', gradient)}>
          <Icon d={iconPath} />
        </div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{label}</span>
      </div>
      <p className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums">{value}</p>
    </div>
  );
}

// ─── Attribute card ─────────────────────────────────────────────────────────

interface AttributeCardProps {
  attribute: VariantAttribute;
  index: number;
  count: number;
  showInactive: boolean;
  busy: boolean;
  onRename: (name: string) => void;
  onToggleActive: (active: boolean) => void;
  onMove: (direction: -1 | 1) => void;
  onAddValues: (values: string[]) => void;
  onUpdateValue: (value: VariantAttributeValue, patch: { value?: string; is_active?: boolean; display_order?: number }) => void;
}

function AttributeCard({
  attribute, index, count, showInactive, busy,
  onRename, onToggleActive, onMove, onAddValues, onUpdateValue,
}: AttributeCardProps) {
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(attribute.name);
  const [valueDraft, setValueDraft] = useState('');
  const [editingValueId, setEditingValueId] = useState<string | null>(null);
  const [valueEditDraft, setValueEditDraft] = useState('');

  const values = attribute.values.filter((v) => showInactive || v.is_active);
  const activeValues = attribute.values.filter((v) => v.is_active);

  function commitName() {
    setEditingName(false);
    const next = nameDraft.trim();
    if (next && next !== attribute.name) onRename(next);
    else setNameDraft(attribute.name);
  }

  function commitValues() {
    const list = valueDraft.split(',').map((v) => v.trim()).filter(Boolean);
    if (list.length === 0) return;
    onAddValues(list);
    setValueDraft('');
  }

  function commitValueEdit(value: VariantAttributeValue) {
    setEditingValueId(null);
    const next = valueEditDraft.trim();
    if (next && next !== value.value) onUpdateValue(value, { value: next });
  }

  function moveValue(value: VariantAttributeValue, direction: -1 | 1) {
    const ordered = [...attribute.values].sort((a, b) => a.display_order - b.display_order || a.value.localeCompare(b.value));
    const i = ordered.findIndex((v) => v.id === value.id);
    const j = i + direction;
    if (i < 0 || j < 0 || j >= ordered.length) return;
    // Re-number sequentially with the swap applied; only changed rows are patched.
    const next = [...ordered];
    [next[i], next[j]] = [next[j], next[i]];
    next.forEach((v, order) => {
      if (v.display_order !== order) onUpdateValue(v, { display_order: order });
    });
  }

  return (
    <div
      className={cn(
        GLASS,
        'p-5 transition-all duration-200 hover:shadow-lg animate-slide-up',
        !attribute.is_active && 'opacity-60',
      )}
    >
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 text-white shadow-sm">
          <Icon d={ICON_TAG} className="h-4.5 w-4.5" />
        </div>
        <div className="min-w-0 flex-1">
          {editingName ? (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                id={`attr-${attribute.id}-name`}
                value={nameDraft}
                onChange={setNameDraft}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitName();
                  if (e.key === 'Escape') { setNameDraft(attribute.name); setEditingName(false); }
                }}
                className="max-w-xs"
                aria-label="Attribute name"
              />
              <Button variant="primary" size="sm" onClick={commitName} disabled={busy}>Save</Button>
              <Button variant="ghost" size="sm" onClick={() => { setNameDraft(attribute.name); setEditingName(false); }}>Cancel</Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { setNameDraft(attribute.name); setEditingName(true); }}
              className="group inline-flex items-center gap-2 text-left"
              title="Rename"
            >
              <span className="text-lg font-semibold text-neutral-900 dark:text-white">{attribute.name}</span>
              <span className="text-xs text-neutral-400 opacity-0 transition-opacity duration-200 group-hover:opacity-100">edit</span>
            </button>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 font-medium text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
              {activeValues.length} {activeValues.length === 1 ? 'value' : 'values'}
            </span>
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2 py-0.5 font-medium',
                (attribute.usage_count ?? 0) > 0
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                  : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400',
              )}
              title="Products whose variants use this attribute"
            >
              Used by {attribute.usage_count ?? 0} {(attribute.usage_count ?? 0) === 1 ? 'product' : 'products'}
            </span>
            {!attribute.is_active && (
              <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                Inactive
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={busy || index === 0}
            className="rounded-lg p-1.5 text-neutral-500 transition-colors duration-200 hover:bg-violet-50 hover:text-violet-700 disabled:opacity-30 dark:hover:bg-violet-900/30"
            aria-label={`Move ${attribute.name} up`}
          >
            <Icon d="M5 15l7-7 7 7" className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={busy || index === count - 1}
            className="rounded-lg p-1.5 text-neutral-500 transition-colors duration-200 hover:bg-violet-50 hover:text-violet-700 disabled:opacity-30 dark:hover:bg-violet-900/30"
            aria-label={`Move ${attribute.name} down`}
          >
            <Icon d="M19 9l-7 7-7-7" className="h-4 w-4" />
          </button>
          <Toggle
            checked={attribute.is_active}
            onChange={onToggleActive}
            label="Active"
            size="sm"
            disabled={busy}
            id={`attr-${attribute.id}-active`}
          />
        </div>
      </div>

      {/* Values */}
      <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
        Switch a value off to stop it being offered for new variants. Existing products keep it.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {values.length === 0 && (
          <span className="text-xs text-neutral-400">No values yet — add some below.</span>
        )}
        {values.map((value) => (
          <span
            key={value.id}
            className={cn(
              'group inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium transition-all duration-200',
              value.is_active
                ? 'bg-violet-100 text-violet-800 hover:bg-violet-200 dark:bg-violet-900/40 dark:text-violet-200 dark:hover:bg-violet-900/60'
                : 'bg-neutral-100 text-neutral-400 line-through dark:bg-neutral-800 dark:text-neutral-500',
            )}
          >
            {editingValueId === value.id ? (
              <input
                autoFocus
                value={valueEditDraft}
                onChange={(e) => setValueEditDraft(e.target.value)}
                onBlur={() => commitValueEdit(value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitValueEdit(value);
                  if (e.key === 'Escape') setEditingValueId(null);
                }}
                className="w-24 rounded bg-white px-1 text-sm text-neutral-900 outline-none ring-1 ring-violet-300 dark:bg-neutral-900 dark:text-white"
                aria-label={`Rename value ${value.value}`}
              />
            ) : (
              <button
                type="button"
                onClick={() => { setValueEditDraft(value.value); setEditingValueId(value.id); }}
                title="Rename"
                className="transition-colors duration-200"
              >
                {value.value}
              </button>
            )}
            {value.is_active && (
              <>
                <button
                  type="button"
                  onClick={() => moveValue(value, -1)}
                  className="hidden text-xs opacity-60 hover:opacity-100 group-hover:inline"
                  aria-label={`Move ${value.value} left`}
                  disabled={busy}
                >
                  ◀
                </button>
                <button
                  type="button"
                  onClick={() => moveValue(value, 1)}
                  className="hidden text-xs opacity-60 hover:opacity-100 group-hover:inline"
                  aria-label={`Move ${value.value} right`}
                  disabled={busy}
                >
                  ▶
                </button>
              </>
            )}
            <span
              className="ml-1 inline-flex items-center"
              title={value.is_active ? `Deactivate ${value.value}` : `Reactivate ${value.value}`}
            >
              <Toggle
                checked={value.is_active}
                onChange={(next) => onUpdateValue(value, { is_active: next })}
                size="sm"
                disabled={busy}
                id={`attr-${attribute.id}-value-${value.id}-active`}
              />
            </span>
          </span>
        ))}
      </div>

      {/* Add values */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Input
          id={`attr-${attribute.id}-newvalue`}
          placeholder="Add values — comma-separated, press Enter"
          value={valueDraft}
          onChange={setValueDraft}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitValues();
            }
          }}
          className="w-72"
        />
        <Button variant="ghost" size="sm" onClick={commitValues} disabled={busy || !valueDraft.trim()}>
          + Add Value
        </Button>
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

function VariantAttributesContent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showInactive, setShowInactive] = useState(false);
  const [newName, setNewName] = useState('');
  const [newValues, setNewValues] = useState('');
  const [formError, setFormError] = useState('');

  const { data: attributes, isLoading } = useQuery({
    queryKey: [...VARIANT_ATTRIBUTES_QUERY_KEY, { includeInactive: showInactive }],
    queryFn: () => variantAttributeService.list({ includeInactive: showInactive }),
  });

  // Parents that actually have variants — one lightweight count query.
  const { data: variantParents } = useQuery({
    queryKey: ['products', 'with-variants-count'],
    queryFn: () => productService.list({ has_variants: 'true', limit: 1 }),
    staleTime: 60_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: VARIANT_ATTRIBUTES_QUERY_KEY });
  };
  const onError = (err: Error) => toast({ title: err.message ?? 'Request failed', variant: 'error' });

  const createMutation = useMutation({
    mutationFn: () =>
      variantAttributeService.create({
        name: newName.trim(),
        values: newValues.split(',').map((v) => v.trim()).filter(Boolean),
        display_order: attributes?.length ?? 0,
      }),
    onSuccess: (created) => {
      invalidate();
      setNewName('');
      setNewValues('');
      setFormError('');
      toast({ title: `Attribute "${created.name}" added`, variant: 'success' });
    },
    onError: (err: Error) => setFormError(err.message ?? 'Could not add attribute'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof variantAttributeService.update>[1] }) =>
      variantAttributeService.update(id, patch),
    onSuccess: invalidate,
    onError,
  });

  const addValuesMutation = useMutation({
    mutationFn: ({ id, values }: { id: string; values: string[] }) => variantAttributeService.addValues(id, values),
    onSuccess: invalidate,
    onError,
  });

  const updateValueMutation = useMutation({
    mutationFn: ({ id, valueId, patch }: { id: string; valueId: string; patch: Parameters<typeof variantAttributeService.updateValue>[2] }) =>
      variantAttributeService.updateValue(id, valueId, patch),
    onSuccess: invalidate,
    onError,
  });

  const busy =
    updateMutation.isPending || addValuesMutation.isPending || updateValueMutation.isPending;

  function handleCreate() {
    if (!newName.trim()) {
      setFormError('Give the attribute a name (e.g. Size)');
      return;
    }
    createMutation.mutate();
  }

  /** Re-number the list sequentially with the swap applied; patch only rows whose order changed. */
  function moveAttribute(index: number, direction: -1 | 1) {
    const list = attributes ?? [];
    const j = index + direction;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    [next[index], next[j]] = [next[j], next[index]];
    next.forEach((attr, order) => {
      if (attr.display_order !== order) updateMutation.mutate({ id: attr.id, patch: { display_order: order } });
    });
  }

  const list = attributes ?? [];
  const activeCount = list.filter((a) => a.is_active).length;
  const valueCount = list.reduce((sum, a) => sum + a.values.filter((v) => v.is_active).length, 0);
  const productsWithVariants = variantParents?.meta?.total ?? 0;

  return (
    <PageContainer
      title="Variant Attributes"
      subtitle="Reusable attributes and values for product variants"
      breadcrumbs={[{ label: 'Products', href: '/products' }, { label: 'Variant Attributes' }]}
      actions={
        <Button variant="secondary" size="sm" onClick={() => navigate('/products')}>
          Back to Products
        </Button>
      }
    >
      {/* ─── Gradient header ─── */}
      <div className="bg-gradient-to-r from-violet-600 to-pink-600 rounded-xl p-6 text-white shadow-lg mb-6 animate-fade-in">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 backdrop-blur">
              <Icon d={ICON_TAG} className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Attribute Library</h2>
              <p className="mt-1 text-sm text-white/85 max-w-xl">
                Define an attribute once — Size, Colour, Gauge — and it is offered when creating variants
                for any product. Anything typed on a product lands here automatically. Deactivated
                entries stay on existing products but disappear from the builder.
              </p>
            </div>
          </div>
          <div className="rounded-lg bg-white/15 px-3 py-2 backdrop-blur">
            <Toggle checked={showInactive} onChange={setShowInactive} label="Show inactive" size="sm" id="show-inactive" />
          </div>
        </div>
      </div>

      {/* ─── KPI strip ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Kpi label="Attributes" value={String(activeCount)} iconPath={ICON_TAG} gradient="from-violet-500 to-pink-500" />
        <Kpi label="Values" value={String(valueCount)} iconPath={ICON_LIST} gradient="from-indigo-500 to-violet-500" />
        <Kpi label="Products with variants" value={String(productsWithVariants)} iconPath={ICON_BOX} gradient="from-teal-500 to-success-500" />
      </div>

      {/* ─── New attribute ─── */}
      <div className={cn(GLASS, 'p-5 mb-6 animate-fade-in')}>
        <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-700 dark:text-neutral-200 mb-3">
          New attribute
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-start">
          <Input
            id="new-attribute-name"
            placeholder="Name, e.g. Size"
            value={newName}
            onChange={(v) => { setNewName(v); setFormError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
          />
          <Input
            id="new-attribute-values"
            placeholder="Values, comma-separated, e.g. S, M, L (optional)"
            value={newValues}
            onChange={setNewValues}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
          />
          <Button variant="primary" onClick={handleCreate} disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Adding…' : '+ Add Attribute'}
          </Button>
        </div>
        {formError && <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">{formError}</p>}
      </div>

      {/* ─── Attribute cards ─── */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className={cn(GLASS, 'p-10 text-center animate-fade-in')}>
          <div className="flex h-14 w-14 mx-auto items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-pink-500 text-white mb-3 shadow-md">
            <Icon d={ICON_TAG} className="h-7 w-7" />
          </div>
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">No variant attributes yet</p>
          <p className="text-xs text-neutral-500 mt-1 max-w-sm mx-auto">
            Add one above, or define variants on a product — its attributes and values will appear here for reuse.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {list.map((attribute, index) => (
            <AttributeCard
              key={attribute.id}
              attribute={attribute}
              index={index}
              count={list.length}
              showInactive={showInactive}
              busy={busy}
              onRename={(name) => updateMutation.mutate({ id: attribute.id, patch: { name } })}
              onToggleActive={(is_active) => updateMutation.mutate({ id: attribute.id, patch: { is_active } })}
              onMove={(direction) => moveAttribute(index, direction)}
              onAddValues={(values) => addValuesMutation.mutate({ id: attribute.id, values })}
              onUpdateValue={(value, patch) =>
                updateValueMutation.mutate({ id: attribute.id, valueId: value.id, patch })
              }
            />
          ))}
        </div>
      )}
    </PageContainer>
  );
}

export function VariantAttributesPage() {
  return (
    <ErrorBoundary>
      <VariantAttributesContent />
    </ErrorBoundary>
  );
}
